import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_STAFF_MANAGEMENT_WORKER_SESSIONS } from "graphql/query/staff-management-gql";
import {
    STAFF_WORKER_SESSIONS_PAGE_SIZE,
    buildStaffWorkerSessionsWhere,
    getStaffWorkerSessionSummaries,
    normalizeStaffWorkerIDs,
} from "../helpers/staff-workers-api-helper";


const WORKER_SESSIONS_ORDER_BY = [
    { column: "ID", order: "ASC" },
];

const getSessionsPage = (result) => result?.data?.workSessions || {};

export const fetchAllStaffWorkerSessions = async ({
    client,
    where,
    perPage = STAFF_WORKER_SESSIONS_PAGE_SIZE,
}) => {
    const queryPage = (page) => client.query({
        query: GET_STAFF_MANAGEMENT_WORKER_SESSIONS,
        variables: {
            where,
            orderBy: WORKER_SESSIONS_ORDER_BY,
            first: perPage,
            page,
        },
        fetchPolicy: "network-only",
    });
    const firstResult = await queryPage(1);
    const firstPage = getSessionsPage(firstResult);
    const lastPage = Math.max(1, Number(firstPage.paginatorInfo?.lastPage) || 1);
    const remainingPages = Array.from(
        { length: Math.max(0, lastPage - 1) },
        (_, index) => index + 2
    );
    const remainingResults = await Promise.all(remainingPages.map(queryPage));

    return [
        ...(firstPage.data || []),
        ...remainingResults.flatMap((result) => getSessionsPage(result).data || []),
    ];
};

const useStaffWorkerSessions = ({ eeID, workers = [] }) => {
    const client = useApolloClient();
    const workerIDsKey = JSON.stringify(normalizeStaffWorkerIDs(workers));
    const workerIDs = useMemo(() => JSON.parse(workerIDsKey), [ workerIDsKey ]);
    const where = useMemo(() => buildStaffWorkerSessionsWhere({
        eeID,
        workerIDs,
    }), [ eeID, workerIDs ]);
    const [ state, setState ] = useState({
        error: null,
        loading: false,
        sessionSummaries: {},
    });

    useEffect(() => {
        let isCurrentRequest = true;

        if (!where)
        {
            setState({
                error: null,
                loading: false,
                sessionSummaries: {},
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        setState({
            error: null,
            loading: true,
            sessionSummaries: {},
        });

        fetchAllStaffWorkerSessions({ client, where })
            .then((sessions) => {
                if (isCurrentRequest)
                {
                    setState({
                        error: null,
                        loading: false,
                        sessionSummaries: getStaffWorkerSessionSummaries(workerIDs, sessions),
                    });
                }
            })
            .catch((error) => {
                if (isCurrentRequest)
                {
                    setState({
                        error,
                        loading: false,
                        sessionSummaries: {},
                    });
                }
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [ client, where, workerIDs ]);

    return state;
};

export default useStaffWorkerSessions;
